import React, { useContext, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { AuthContext } from '../context/AuthContext';
import '../css/Sidebar.css';
import logo from '../assets/omega_logo.jpg'; // Adjust the path as necessary

export const Sidebar = ({ mobileOpen, setMobileOpen }) => {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [openGroups, setOpenGroups] = useState({
    accounts: true,
    inventory: false,
    admin: false
  });

  const toggleGroup = (group) => {
    setOpenGroups(prev => ({ ...prev, [group]: !prev[group] }));
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (path) => {
    const currentPath = location.pathname;
    if (currentPath === path) return true;
    if (path === '/sales' && currentPath.startsWith('/customer-sales/')) return true;
    if (path === '/clients' && (
      currentPath.startsWith('/clients-by-officer/') ||
      currentPath.startsWith('/client-details/') ||
      currentPath.startsWith('/client-')
    )) return true;
    if (path === '/purchases' && currentPath.startsWith('/purchase-details/')) return true;
    if (path === '/returns' && currentPath.startsWith('/return-details/')) return true;
    return false;
  };

  const role = user?.role?.toLowerCase();

  // Define Menu Groups
  const menuGroups = [
    {
      id: 'main',
      items: [
        { path: '/dashboard', icon: 'bi bi-graph-up', label: 'Dashboard' },
      ]
    },
    {
      id: 'accounts',
      label: 'Accounts & Transactions',
      icon: 'bi bi-wallet2',
      roles: ['admin', 'sales_manager', 'purchase_manager', 'normal_user'],
      items: [
        { path: '/clients', icon: 'bi bi-people', label: 'Client Management', roles: ['admin', 'sales_manager', 'purchase_manager', 'normal_user'] },
        { path: '/sales', icon: 'bi bi-cash-coin', label: 'Sales Account', roles: ['admin', 'sales_manager', 'normal_user'] },
        { path: '/purchases', icon: 'bi bi-box-seam', label: 'Purchase Account', roles: ['admin', 'purchase_manager', 'normal_user'] },
        { path: '/returns', icon: 'bi bi-arrow-counterclockwise', label: 'Returns', roles: ['admin', 'sales_manager', 'purchase_manager', 'normal_user'] },
        { path: '/payments', icon: 'bi bi-cash-stack', label: 'Payment Records', roles: ['admin'] },
        { path: '/major-client-report', icon: 'bi bi-file-earmark-bar-graph', label: 'Major Client Report', roles: ['admin', 'sales_manager', 'normal_user'] },
        { path: '/overall-sales', icon: 'bi bi-file-earmark-spreadsheet', label: 'Overall Sales Report', roles: ['admin', 'sales_manager', 'normal_user'] },
        { path: '/overall-purchases', icon: 'bi bi-file-earmark-spreadsheet-fill', label: 'Overall Purchase Report', roles: ['admin', 'purchase_manager', 'normal_user'] },
        { path: '/month-wise-balance-report', icon: 'bi bi-calendar-check', label: 'Monthly Balance Report', roles: ['admin', 'sales_manager', 'purchase_manager', 'normal_user'] },
      ]
    },
    {
      id: 'inventory',
      label: 'Inventory',
      icon: 'bi bi-box-seam',
      roles: ['admin', 'sales_manager', 'purchase_manager', 'normal_user'],
      items: [
        { path: '/products', icon: 'bi bi-box', label: 'Products', roles: ['admin', 'sales_manager', 'purchase_manager', 'normal_user'] },
        { path: '/raw-materials', icon: 'bi bi-boxes', label: 'Raw Materials', roles: ['admin', 'sales_manager', 'purchase_manager', 'normal_user'] },
      ]
    },
    {
      id: 'administration',
      label: 'Administration',
      icon: 'bi bi-gear',
      roles: ['admin', 'sales_manager', 'purchase_manager', 'normal_user'],
      items: [
        { path: '/branches', icon: 'bi bi-shop', label: 'Branches', roles: ['admin', 'sales_manager', 'purchase_manager', 'normal_user'] },
        { path: '/company-accounts', icon: 'bi bi-bank', label: 'Company Accounts', roles: ['admin', 'sales_manager', 'purchase_manager', 'normal_user'] },
        { path: '/executive-officers', icon: 'bi bi-briefcase', label: 'Executive Officers', roles: ['admin', 'sales_manager', 'purchase_manager', 'normal_user'] },
        { path: '/users', icon: 'bi bi-person-badge', label: 'User Management', roles: ['admin'] },
      ]
    }
  ];

  // Helper to filter items based on role
  const filterByRole = (items) => {
    if (!role) return [];
    if (role === 'admin') return items;
    return items.filter(item => !item.roles || item.roles.includes(role));
  };

  return (
    <>
      {/* Mobile Backdrop Overlay */}
      {mobileOpen && (
        <div 
          className="sidebar-backdrop d-md-none" 
          onClick={() => setMobileOpen(false)}
          style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000 }}
        ></div>
      )}

      <div className={`sidebar ${isCollapsed ? 'collapsed' : ''} ${mobileOpen ? 'mobile-open' : ''}`}>

      {/* Sidebar Header */}
      <div className="sidebar-header d-flex justify-content-between align-items-center">
        <div className="sidebar-brand">
          {!isCollapsed && (
            <div className="d-flex align-items-center">
              <img
                src={logo}
                alt="Omega Logo"
                className="sidebar-logo me-2"
                style={{ height: '40px', width: '40px', objectFit: 'contain' }}
              />
            </div>
          )}
        </div>

        <button
          className="btn btn-sm toggle-btn d-none d-md-block"
          onClick={() => setIsCollapsed(!isCollapsed)}
          data-tooltip={isCollapsed ? 'Expand' : 'Collapse'}
        >
          <i className={`bi ${isCollapsed ? 'bi-chevron-right' : 'bi-chevron-left'}`}></i>
        </button>
        <button
          className="btn btn-sm toggle-btn d-md-none text-white border-0"
          onClick={() => setMobileOpen(false)}
          data-tooltip="Close Sidebar"
        >
          <i className="bi bi-x-lg"></i>
        </button>
      </div>

      {/* User Profile */}
      {!isCollapsed && (
        <div className="sidebar-user p-3">
          <div className="d-flex align-items-center">
            <div className="user-avatar bg-light text-primary rounded-circle d-flex align-items-center justify-content-center me-3">
              <i className="bi bi-person-circle fs-5"></i>
            </div>
            <div className="user-info flex-grow-1">
              <p className="user-name fw-600 mb-0 text-white">{user?.full_name}</p>
              <p className="user-role text-muted small mb-0">{user?.role}</p>
            </div>
          </div>
        </div>
      )}

      {/* Navigation Menu */}
      <nav className="sidebar-nav flex-grow-1 overflow-auto">
        <ul className="nav flex-column">
          {menuGroups.map((group) => {
            const visibleItems = filterByRole(group.items);
            if (visibleItems.length === 0) return null;

            if (!group.label) {
              // Group without label (like Dashboard)
              return visibleItems.map(item => (
                <li className="nav-item" key={item.path}>
                  <OverlayTrigger
                    placement="right"
                    overlay={
                      <Tooltip id={`tooltip-${item.path}`} className="premium-sidebar-tooltip">
                        {item.label}
                      </Tooltip>
                    }
                    show={isCollapsed ? undefined : false}
                  >
                    <button
                      className={`nav-link d-flex align-items-center w-100 ${isActive(item.path) ? 'active' : ''}`}
                      onClick={() => {
                          navigate(item.path);
                          if (mobileOpen !== undefined) setMobileOpen(false);
                      }}
                    >
                      <i className={`${item.icon} nav-icon me-3 fs-5`}></i>
                      {!isCollapsed && <span className="nav-label flex-grow-1 text-start">{item.label}</span>}
                    </button>
                  </OverlayTrigger>
                </li>
              ));
            }

            const isGroupOpen = openGroups[group.id];
            const hasActiveItem = visibleItems.some(item => isActive(item.path));

            return (
              <li className="nav-group-container" key={group.id}>
                {!isCollapsed && (
                  <button
                    className={`nav-group-header w-100 ${hasActiveItem ? 'child-active' : ''}`}
                    onClick={() => toggleGroup(group.id)}
                  >
                    <i className={`${group.icon} group-icon me-3`}></i>
                    <span className="group-label flex-grow-1 text-start">{group.label}</span>
                    <i className={`bi bi-chevron-${isGroupOpen ? 'down' : 'left'} toggle-icon`}></i>
                  </button>
                )}

                {(isGroupOpen || isCollapsed) && (
                  <ul className="nav flex-column nav-subitems">
                    {visibleItems.map(item => (
                      <li className="nav-item" key={item.path}>
                        <OverlayTrigger
                          placement="right"
                          overlay={
                            <Tooltip id={`tooltip-sub-${item.path}`} className="premium-sidebar-tooltip">
                              {item.label}
                            </Tooltip>
                          }
                          show={isCollapsed ? undefined : false}
                        >
                          <button
                            className={`nav-link d-flex align-items-center w-100 ${isActive(item.path) ? 'active' : ''}`}
                            onClick={() => {
                                navigate(item.path);
                                if (mobileOpen !== undefined) setMobileOpen(false);
                            }}
                          >
                            <i className={`${item.icon} nav-icon ${isCollapsed ? 'me-3' : 'me-3 sub-icon'} fs-5`}></i>
                            {!isCollapsed && <span className="nav-label flex-grow-1 text-start">{item.label}</span>}
                          </button>
                        </OverlayTrigger>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Logout */}
      <div className="sidebar-footer p-3 border-top">
        <OverlayTrigger
          placement="right"
          overlay={
            <Tooltip id="tooltip-logout" className="premium-sidebar-tooltip">
              Logout
            </Tooltip>
          }
          show={isCollapsed ? undefined : false}
        >
          <button
            className="btn btn-outline-danger w-100 d-flex align-items-center justify-content-center"
            onClick={handleLogout}
          >
            <i className="bi bi-box-arrow-right me-2"></i>
            {!isCollapsed && <span>Logout</span>}
          </button>
        </OverlayTrigger>
      </div>
    </div>
    </>
  );
};